// client/table/data-table.ts
import { css } from "@adornis/chemistry/directives/css.js";
import { virtualize } from "@adornis/chemistry/directives/when-seen.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/elements/components/x-grid.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import { makeUniqueIDForFieldConf } from "@adornis/field-management/field-configuration.js";
import { stateful } from "@adornis/functional-lit/stateful.js";
import { goTo } from "@adornis/router/client/open-href.js";
import { html, nothing } from "lit";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { getFieldFromEntity } from "../../get-field-from-entity.js";
import { buildFullOptions } from "../data-view-options.js";
import { useDataloading } from "../use-data-loading.js";
import { DataViewHeader } from "./header-line.js";
var BORDER_COLOR = "color-mix(in srgb, var(--color-base-content), transparent 90%)";
var DataTable = stateful(
  (props) => {
    const options = buildFullOptions(props.options).dataViewOptions;
    const { data, loadingInitially, complete, next, totalRequested } = useDataloading(options);
    const gridRef = createRef();
    const finalColumns = options.finalColumns();
    const renderField = (entityInstance, colDef, indexColumn, indexRow) => {
      const href = options.rowHref(entityInstance);
      const content = colDef.renderItem ? (
        // @ts-expect-error assigning null without changing the type for now
        colDef.renderItem(entityInstance, null, indexRow)
      ) : fallbackRenderItem(getFieldFromEntity(entityInstance, colDef));
      return html`
        <div
          class="flex items-center cell min-w-0"
          ${css({
        position: colDef.isSticky ? "sticky" : "unset",
        [colDef.position === "back" ? "right" : "left"]: colDef.isSticky ? colDef.position === "back" ? "0" : getLeftOffsetForStackedStickyColumns(colDef, indexColumn) : "unset",
        zIndex: colDef.isSticky ? "10" : "unset"
      })}
          ?active=${options.view?.selectedItems?.includes(
        entityInstance.constructor.resolveID(entityInstance)
      )}
        >
          ${href ? html`<a
                href="${href}"
                class="no-underline text-inherit hover:cursor-pointer"
                @click=${(e) => {
        e.preventDefault();
        goTo(href);
      }}
              >
                ${content}
              </a>` : content}
        </div>
      `;
    };
    const getLeftOffsetForStackedStickyColumns = (colDef, colIndex) => {
      if (!colDef.isSticky)
        return "0px";
      const leftOffset = finalColumns.slice(0, colIndex).reduce((acc, curr) => acc + +curr.columnWidth.replace("px", "") + 3, 0);
      return leftOffset.toString() + "px";
    };
    const fallbackRenderItem = (value) => {
      if (value === void 0 || value === null)
        return "";
      if (value instanceof Object && "_$litType$" in value)
        return value;
      if (value instanceof Object && "_$litDirective$" in value)
        return value;
      if (typeof value === "object")
        return JSON.stringify(value);
      return value.toString();
    };
    const renderedIndexCallback = (indexRow) => {
      if (indexRow === totalRequested - 5 && !complete)
        next();
    };
    const header = DataViewHeader({
      options,
      finalColumns,
      gridElement: gridRef,
      onSort: (e) => {
        const { columnFieldName, sortingDirection, columnIndex } = e.detail;
        if (options.view?.filter) {
          if (sortingDirection === void 0) {
            options.view.filter.sortingFields.splice(columnIndex, 1);
            options.view.filter.sortingDirections.splice(columnIndex, 1);
          } else {
            options.view.filter.sortingFields[columnIndex] = columnFieldName;
            options.view.filter.sortingDirections[columnIndex] = sortingDirection;
          }
        }
      },
      onColumnsUpdated: (colWidths) => {
        finalColumns.forEach((colDef, index) => {
          const confCol = options.view?.configuredColumns?.find(
            (cc) => makeUniqueIDForFieldConf(cc) === makeUniqueIDForFieldConf(colDef)
          );
          if (!confCol)
            return;
          if (confCol.columnWidth !== colWidths[index] + "px")
            confCol.columnWidth = colWidths[index] + "px";
        });
      }
    });
    return html`
      <div
        class="grid text-xs"
        ${ref(gridRef)}
        ${css({
      overflowAnchor: "none",
      gridTemplateColumns: finalColumns.map((col) => col.columnWidth ?? "1fr").join(" ")
    })}
      >
        ${header}
        ${data.length ? html`
              ${repeat(
      data,
      (entityInstance) => entityInstance.constructor.resolveID(entityInstance),
      (entityInstance, indexRow) => {
        const groupHeader = options.renderGroupHeader?.(entityInstance, data[indexRow - 1]) ?? nothing;
        return html`
                    ${groupHeader}
                    ${virtualize(
          html`
                        ${repeat(
            finalColumns,
            (colDef) => colDef,
            (colDef, indexColumn) => xComponents(
              html`
                                <div
                                  class="flex items-center cell py-2 px-4 border-t-1 min-w-0"
                                  ${css({ borderColor: BORDER_COLOR })}
                                  @click=${() => {
                if (options.isEntryClickable) {
                  options.view?.toggleSelection(
                    entityInstance.constructor.resolveID(entityInstance)
                  );
                  options.entryClickHandler?.(entityInstance, colDef);
                  gridRef.value?.dispatchEvent(
                    new CustomEvent("entry-clicked", {
                      detail: { entity: entityInstance, column: colDef },
                      bubbles: true,
                      composed: true
                    })
                  );
                }
              }}
                                >
                                  ${renderField(entityInstance, colDef, indexColumn, indexRow)}
                                </div>
                              `
            )
          )}
                      `,
          (oldHeight) => html` <div class="col-span-full" ${css({ height: `${oldHeight}px` })}></div> `,
          {
            wrapperElem: () => {
              const div = document.createElement("div");
              div.className = `col-span-full grid grid-cols-subgrid ${options.isEntryClickable ? "cursor-pointer hover:bg-base-200" : ""}`;
              return div;
            },
            onRender: () => renderedIndexCallback(indexRow)
          }
        )}
                  `;
      }
    )}
            ` : !loadingInitially ? options.emptyState() : nothing}
      </div>

      ${loadingInitially ? html`<div class="flex justify-center items-center col-span-full">${options.loader()}</div> ` : nothing}
    `;
  }
);
export {
  DataTable
};
